import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useEffect, useId, useState } from "react";
import { type AddressSearch, type AddressSuggestion, distanceMeters } from "./addressApi";
import { directionsUrl, osmEmbedUrl, osmLinkUrl, placeLinkUrl } from "./mapUrls";

/** ~100 m — a move beyond this warns that the public site map will visibly shift (R3). */
const SIGNIFICANT_MOVE_M = 100;
/** One nudge step (~5 m) for keyboard fine-tuning of the pin without a drag surface. */
const NUDGE_STEP = 0.00005;
const DEBOUNCE_MS = 300;

export interface VenueLocationValue {
  streetLine1: string;
  streetLine2: string;
  postalCode: string;
  locality: string;
  region: string;
  country: string;
  lat: number;
  lng: number;
  formattedAddress: string;
}

export interface VenueLocationSectionProps {
  /** Injected proxy search (auth wired by the composing screen). */
  search: AddressSearch;
  initial?: Partial<VenueLocationValue> & { placeId?: string | null; placeStatus?: string | null };
  /** True after a text-only edit until the pin is re-confirmed (R3 banner). */
  reconfirmRequired?: boolean;
  saving?: boolean;
  /**
   * Onboarding-friendly mode: hide the technical bits a restaurateur doesn't need — the country ISO-code
   * and region fields, the raw GPS coordinates, the keyboard pin-nudge, and the OSM map/link-outs. Country
   * is forced to "FR". The full experience (all of the above) is the default for the settings surface.
   */
  compact?: boolean;
  onConfirm: (value: VenueLocationValue) => void;
}

interface Fields {
  streetLine1: string;
  streetLine2: string;
  postalCode: string;
  locality: string;
  region: string;
  country: string;
}

function oneLine(f: Fields): string {
  const cityLine = `${f.postalCode} ${f.locality}`.trim();
  return [f.streetLine1, cityLine, f.country].filter((p) => p.trim().length > 0).join(", ");
}

/**
 * The composable venue-location section (`venue-address-and-map`): a BAN autocomplete combobox
 * (native `input[list]` + `datalist` — keyboard/AT accessible by default) → structured fields + a
 * dropped pin, keyboard pin fine-tuning, a manual fallback (degraded / not found), the click-to-load
 * OSM embed (consent-gated — no tile request until the manager asks), and keyless directions / place
 * link-outs. `tasks/venue-onboarding.md` mounts this inside the wizard.
 */
export function VenueLocationSection({
  search,
  initial,
  reconfirmRequired = false,
  saving = false,
  compact = false,
  onConfirm,
}: VenueLocationSectionProps) {
  const { t } = useTranslation();
  const listId = useId();

  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [degraded, setDegraded] = useState(false);
  const [matchedHint, setMatchedHint] = useState(false);
  const [mapConsented, setMapConsented] = useState(false);
  const [locating, setLocating] = useState(false);

  const [fields, setFields] = useState<Fields>({
    streetLine1: initial?.streetLine1 ?? "",
    streetLine2: initial?.streetLine2 ?? "",
    postalCode: initial?.postalCode ?? "",
    locality: initial?.locality ?? "",
    region: initial?.region ?? "",
    country: initial?.country ?? "FR",
  });
  const [pin, setPin] = useState<{ lat: number; lng: number } | null>(
    initial?.lat !== undefined && initial?.lng !== undefined
      ? { lat: initial.lat, lng: initial.lng }
      : null,
  );

  const initialPin =
    initial?.lat !== undefined && initial?.lng !== undefined
      ? { lat: initial.lat, lng: initial.lng }
      : null;

  // Below the BAN min query length, the list reads as empty regardless of the last fetched suggestions.
  const visibleSuggestions = query.trim().length < 3 ? [] : suggestions;

  // Debounced autocomplete over the injected proxy. < 3 chars clears the list (BAN min query) — derived
  // above via `visibleSuggestions` rather than reset with a synchronous setState in the effect.
  useEffect(() => {
    if (query.trim().length < 3) return;
    const controller = new AbortController();
    const handle = setTimeout(() => {
      void search(query, controller.signal).then((result) => {
        setSuggestions(result.suggestions);
        setDegraded(result.degraded);
      });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  }, [query, search]);

  function selectSuggestion(s: AddressSuggestion) {
    setFields({
      streetLine1: s.streetLine1,
      streetLine2: "",
      postalCode: s.postalCode,
      locality: s.locality,
      region: s.region ?? "",
      country: s.country,
    });
    setPin({ lat: s.lat, lng: s.lng });
    setMatchedHint(true);
  }

  function onQueryChange(value: string) {
    setQuery(value);
    // A datalist pick sets the input to the option's full label — map it back to the suggestion.
    const match = visibleSuggestions.find((s) => s.label === value);
    if (match) selectSuggestion(match);
  }

  function nudge(dLat: number, dLng: number) {
    setPin((p) => (p ? { lat: p.lat + dLat, lng: p.lng + dLng } : p));
    setMatchedHint(false);
  }

  function setField(key: keyof Fields, value: string) {
    setFields((f) => ({ ...f, [key]: value }));
  }

  const moved = pin && initialPin ? distanceMeters(initialPin, pin) > SIGNIFICANT_MOVE_M : false;
  const canConfirm =
    !saving &&
    pin !== null &&
    fields.streetLine1.trim().length > 0 &&
    fields.postalCode.trim().length > 0 &&
    fields.locality.trim().length > 0 &&
    fields.country.trim().length === 2;

  function confirm() {
    if (!pin) return;
    onConfirm({ ...fields, lat: pin.lat, lng: pin.lng, formattedAddress: oneLine(fields) });
  }

  // Fallback when the user typed/filled an address but never got a pin (a native-datalist pick that
  // didn't round-trip, or they skipped the dropdown): geocode the current one-line address on demand and
  // adopt the top match's pin — filling only blank fields — so Save is never a dead-end. Returns nothing
  // (stays degraded) only if the provider itself has nothing, which is a real outage.
  async function locate() {
    const q = oneLine(fields);
    if (q.trim().length < 3) return;
    setLocating(true);
    try {
      const result = await search(q);
      const top = result.suggestions[0];
      if (top) {
        setFields((f) => ({
          streetLine1: f.streetLine1 || top.streetLine1,
          streetLine2: f.streetLine2,
          postalCode: f.postalCode || top.postalCode,
          locality: f.locality || top.locality,
          region: f.region || (top.region ?? ""),
          country: f.country || top.country,
        }));
        setPin({ lat: top.lat, lng: top.lng });
        setMatchedHint(true);
      } else {
        setDegraded(true);
      }
    } finally {
      setLocating(false);
    }
  }

  const placeId = initial?.placeId ?? null;

  return (
    <section className={css({ display: "flex", flexDirection: "column", gap: "4", maxW: "2xl" })}>
      <h2 className={css({ fontSize: "lg", fontWeight: "semibold" })}>
        {t("venueLocation.title")}
      </h2>

      {reconfirmRequired ? (
        <output className={css({ display: "block", color: "fg.warning", fontSize: "sm" })}>
          {t("venueLocation.reconfirmRequired")}
        </output>
      ) : null}

      {/* --- BAN autocomplete combobox (native datalist) ------------------------------------- */}
      <div className={css({ display: "flex", flexDirection: "column", gap: "1" })}>
        <label htmlFor="venue-address-search" className={css({ fontSize: "sm" })}>
          {t("venueLocation.searchLabel")}
        </label>
        <input
          id="venue-address-search"
          type="text"
          list={listId}
          value={query}
          placeholder={t("venueLocation.searchPlaceholder")}
          onChange={(e) => onQueryChange(e.target.value)}
          className={css({ w: "full", px: "3", py: "2", borderWidth: "1px", rounded: "md" })}
        />
        <datalist id={listId}>
          {visibleSuggestions.map((s, i) => (
            <option key={s.providerId || `${s.label}-${i}`} value={s.label}>
              {s.label}
            </option>
          ))}
        </datalist>
        {degraded ? (
          <output className={css({ display: "block", fontSize: "sm", color: "fg.muted" })}>
            {t("venueLocation.degraded")}
          </output>
        ) : null}
      </div>

      {/* --- structured / manual fields ------------------------------------------------------ */}
      <fieldset
        className={css({
          display: "grid",
          gridTemplateColumns: { base: "1fr", sm: "repeat(2, 1fr)" },
          gap: "3",
        })}
      >
        <legend className={css({ fontSize: "sm", fontWeight: "medium", mb: "2" })}>
          {t("venueLocation.addressLegend")}
        </legend>
        <TextField
          label={t("venueLocation.streetLine1")}
          value={fields.streetLine1}
          onChange={(v) => setField("streetLine1", v)}
          span2
        />
        <TextField
          label={t("venueLocation.streetLine2")}
          value={fields.streetLine2}
          onChange={(v) => setField("streetLine2", v)}
          span2
        />
        <TextField
          label={t("venueLocation.postalCode")}
          value={fields.postalCode}
          onChange={(v) => setField("postalCode", v)}
        />
        <TextField
          label={t("venueLocation.locality")}
          value={fields.locality}
          onChange={(v) => setField("locality", v)}
        />
        {!compact && (
          <TextField
            label={t("venueLocation.region")}
            value={fields.region}
            onChange={(v) => setField("region", v)}
          />
        )}
        {!compact && (
          <TextField
            label={t("venueLocation.country")}
            value={fields.country}
            onChange={(v) => setField("country", v.toUpperCase().slice(0, 2))}
          />
        )}
      </fieldset>

      {/* --- pin + click-to-load OSM embed --------------------------------------------------- */}
      {pin ? (
        compact ? (
          <p className={css({ fontSize: "sm", fontWeight: "medium", color: "fg.success" })}>
            ✓ {t("venueLocation.located")}
          </p>
        ) : (
          <div className={css({ display: "flex", flexDirection: "column", gap: "2" })}>
            <p className={css({ fontSize: "sm" })}>
              {t("venueLocation.pin")}: {pin.lat.toFixed(6)}, {pin.lng.toFixed(6)}
            </p>
            {matchedHint ? (
              <p className={css({ fontSize: "xs", color: "fg.muted" })}>
                {t("venueLocation.matchedHint")}
              </p>
            ) : null}
            {moved ? (
              <output className={css({ display: "block", fontSize: "sm", color: "fg.warning" })}>
                {t("venueLocation.significantMove")}
              </output>
            ) : null}

            <fieldset className={css({ display: "flex", gap: "2", alignItems: "center" })}>
              <legend className={css({ fontSize: "xs", color: "fg.muted", mb: "1" })}>
                {t("venueLocation.nudgeGroup")}
              </legend>
              <NudgeButton
                label={t("venueLocation.nudgeNorth")}
                onClick={() => nudge(NUDGE_STEP, 0)}
              />
              <NudgeButton
                label={t("venueLocation.nudgeSouth")}
                onClick={() => nudge(-NUDGE_STEP, 0)}
              />
              <NudgeButton
                label={t("venueLocation.nudgeWest")}
                onClick={() => nudge(0, -NUDGE_STEP)}
              />
              <NudgeButton
                label={t("venueLocation.nudgeEast")}
                onClick={() => nudge(0, NUDGE_STEP)}
              />
            </fieldset>

            {mapConsented ? (
              <iframe
                title={t("venueLocation.mapTitle")}
                src={osmEmbedUrl(pin)}
                sandbox="allow-scripts allow-popups"
                className={css({ w: "full", h: "80", borderWidth: "1px", rounded: "md" })}
                loading="lazy"
              />
            ) : (
              <div
                className={css({
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "2",
                  p: "6",
                  borderWidth: "1px",
                  borderStyle: "dashed",
                  rounded: "md",
                  bg: "bg.subtle",
                })}
              >
                <p className={css({ fontSize: "sm", color: "fg.muted", textAlign: "center" })}>
                  {t("venueLocation.mapConsent")}
                </p>
                <button
                  type="button"
                  onClick={() => setMapConsented(true)}
                  className={css({ px: "3", py: "2", borderWidth: "1px", rounded: "md" })}
                >
                  {t("venueLocation.showMap")}
                </button>
              </div>
            )}

            <div className={css({ display: "flex", gap: "4", fontSize: "sm" })}>
              <a href={directionsUrl(pin)} target="_blank" rel="noopener noreferrer">
                {t("venueLocation.directions")}
              </a>
              <a href={osmLinkUrl(pin)} target="_blank" rel="noopener noreferrer">
                {t("venueLocation.viewOsm")}
              </a>
              {placeId ? (
                <a href={placeLinkUrl(pin, placeId)} target="_blank" rel="noopener noreferrer">
                  {t("venueLocation.viewPlace")}
                </a>
              ) : null}
            </div>
          </div>
        )
      ) : (
        <div
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "2",
            alignItems: "flex-start",
          })}
        >
          <p className={css({ fontSize: "sm", color: "fg.muted" })}>{t("venueLocation.noPin")}</p>
          {/* Geocode-on-demand so a typed address that never produced a datalist pin can still be saved. */}
          <button
            type="button"
            disabled={locating || oneLine(fields).trim().length < 3}
            onClick={() => void locate()}
            className={css({
              px: "3",
              py: "2",
              borderWidth: "1px",
              rounded: "md",
              cursor: "pointer",
              _disabled: { opacity: "0.5", cursor: "not-allowed" },
            })}
          >
            {locating ? t("venueLocation.locating") : t("venueLocation.locate")}
          </button>
        </div>
      )}

      <button
        type="button"
        disabled={!canConfirm}
        onClick={confirm}
        className={css({
          alignSelf: "flex-start",
          px: "4",
          py: "2",
          rounded: "md",
          bg: "colorPalette.default",
          color: "colorPalette.fg",
          _disabled: { opacity: "0.5", cursor: "not-allowed" },
        })}
      >
        {saving ? t("venueLocation.saving") : t("venueLocation.confirm")}
      </button>
    </section>
  );
}

interface TextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  span2?: boolean;
}

function TextField({ label, value, onChange, span2 = false }: TextFieldProps) {
  const id = useId();
  return (
    <div
      className={css({
        display: "flex",
        flexDirection: "column",
        gap: "1",
        gridColumn: span2 ? "span 2" : "auto",
      })}
    >
      <label htmlFor={id} className={css({ fontSize: "sm" })}>
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={css({ px: "3", py: "2", borderWidth: "1px", rounded: "md" })}
      />
    </div>
  );
}

function NudgeButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={css({ px: "3", py: "1", borderWidth: "1px", rounded: "md", fontSize: "sm" })}
    >
      {label}
    </button>
  );
}
